import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ServicesGrid() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  const services = [
    {
      title: 'Equipment Leasing',
      description: 'Mobile MRI and CT units available on flexible short-term and long-term lease terms. Rapid deployment nationwide with transparent pricing.',
      image: 'https://public.readdy.ai/ai/img_res/760f08a72c553d510e09ffa2898d2ad5.jpg',
      features: [
        'Short & long-term lease contracts',
        'MRI, CT, Ultrasound & X-Ray units',
        'No capital expenditure required'
      ]
    },
    {
      title: 'Deployment & Logistics',
      description: 'Complete delivery, positioning, and site setup of imaging equipment. We coordinate power, placement, and site readiness so your team can focus on operations.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20logistics%20team%20positioning%20large%20mobile%20medical%20imaging%20trailer%20at%20hospital%20loading%20dock%20with%20heavy%20equipment%20transport%20truck%20and%20site%20coordination%20showing%20equipment%20delivery%20and%20infrastructure%20setup%20with%20clean%20industrial%20background&width=600&height=560&seq=deploy-logistics-card-001&orientation=portrait',
      features: [
        'Equipment delivery & positioning',
        'Power & site readiness coordination',
        'Placement consultation included'
      ]
    },
    {
      title: 'Maintenance & Support',
      description: 'Preventative maintenance programs and equipment uptime monitoring. Our technical team services the equipment — your clinical staff operates it.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20equipment%20maintenance%20technician%20in%20uniform%20inspecting%20and%20servicing%20large%20mobile%20MRI%20machine%20components%20with%20technical%20tools%20showing%20preventative%20maintenance%20and%20uptime%20support%20for%20medical%20imaging%20hardware%20with%20clean%20workshop%20background&width=600&height=560&seq=maintenance-card-001&orientation=portrait',
      features: [
        'Preventative maintenance programs',
        'Equipment uptime monitoring',
        'Technical servicing (equipment only)'
      ]
    },
    {
      title: 'Flexible Equipment Programs',
      description: 'Interim solutions during construction or downtime, capacity overflow support, and disaster recovery deployment for healthcare facilities.',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20medical%20imaging%20trailer%20being%20deployed%20at%20hospital%20construction%20site%20showing%20interim%20equipment%20solution%20during%20facility%20renovation%20with%20construction%20barriers%20and%20hospital%20building%20in%20background%20with%20professional%20logistics%20setup&width=600&height=560&seq=flexible-programs-card-001&orientation=portrait',
      features: [
        'Interim construction coverage',
        'Capacity overflow solutions',
        'Disaster recovery deployment'
      ]
    }
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 sm:mb-20 transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
            WHAT WE PROVIDE
          </p>
          <h2 className="text-navy text-[28px] sm:text-[36px] lg:text-[44px] font-semibold leading-tight mb-4">
            Equipment Leasing &amp; Infrastructure Support
          </h2>
          <p className="text-[#5A6C7D] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            We provide the equipment and infrastructure. Your team provides the clinical expertise.
          </p>
        </div>

        {/* Services Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
          data-product-shop
        >
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl border border-gray-100 hover:border-slate-blue/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden group cursor-pointer ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
              }`}
              style={{ transitionDelay: gridVisible ? `${index * 150}ms` : '0ms' }}
            >
              {/* Image Container */}
              <div className="relative h-[200px] sm:h-[240px] lg:h-[260px] overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-all duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 lg:p-7">
                <h3 className="text-navy text-xl sm:text-[22px] font-semibold mb-2">
                  {service.title}
                </h3>
                <p className="text-[#5A6C7D] text-sm sm:text-[14px] leading-relaxed mb-4 sm:mb-5">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2 mb-5 sm:mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-slate-blue text-base"></i>
                      </div>
                      <span className="text-[#5A6C7D] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Link */}
                <Link
                  to="/services"
                  className="text-slate-blue text-[14px] font-medium inline-flex items-center gap-2 hover:underline cursor-pointer group/link"
                >
                  Learn More
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover/link:translate-x-1"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
